import * as React from 'react';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'circular' | 'rectangular';
  animation?: 'pulse' | 'shimmer' | 'none';
  width?: string | number;
  height?: string | number;
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      className,
      variant = 'default',
      animation = 'shimmer',
      width,
      height,
      style,
      ...props
    },
    ref
  ) => {
    const variantStyles = {
      default: 'rounded-md',
      circular: 'rounded-full',
      rectangular: 'rounded-none',
    };

    const animationStyles = {
      pulse: 'animate-pulse bg-muted',
      shimmer: 'skeleton',
      none: 'bg-muted',
    };

    return (
      <div
        ref={ref}
        className={`${variantStyles[variant]} ${animationStyles[animation]} ${className || ''}`}
        style={{
          width: width,
          height: height,
          ...style,
        }}
        {...props}
      />
    );
  }
);
Skeleton.displayName = 'Skeleton';

// Preset skeletons for common use cases
function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={`space-y-2 ${className || ''}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height="1rem"
          style={{ width: i === lines - 1 ? '60%' : '100%' }}
        />
      ))}
    </div>
  );
}

function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={`rounded-lg border border-border p-4 ${className || ''}`}>
      <Skeleton height="8rem" className="mb-4" />
      <Skeleton height="1.25rem" className="mb-2" style={{ width: '80%' }} />
      <Skeleton height="1rem" style={{ width: '60%' }} />
    </div>
  );
}

function SkeletonAvatar({ size = 40 }: { size?: number }) {
  return <Skeleton variant="circular" width={size} height={size} />;
}

function SkeletonButton({ width = 100, height = 36 }: { width?: number; height?: number }) {
  return <Skeleton width={width} height={height} />;
}

export { Skeleton, SkeletonText, SkeletonCard, SkeletonAvatar, SkeletonButton };
